import React, { useMemo } from 'react';
import _ from 'lodash';
import { withLocalize } from 'react-localize-redux';
import { useLocalize } from '../../hooks';
const externalLinkTextId = 'externalLink';
/**
 * Gets the subdirectory prefix (e.g., '/TBFE-1955-2' or '').
 * @returns The subdirectory prefix (e.g., '/TBFE-1955-2' or '').
 */
const getSubdirectory = () => {
    if (typeof window !== 'undefined' && window.__TB_SUBDIRECTORY__) {
        const subdirectory = window.__TB_SUBDIRECTORY__;
        return subdirectory.endsWith('/') ? subdirectory.slice(0, -1) : subdirectory;
    }
    return '';
};
/**
 * Subdomain labels for hosts that serve RestHub.
 * Matches REACT_APP_TRADING_API_URL / TB_RESTHUB_URL hosts such as devapi.tradingblock.com.
 * Links to this host must keep their original path; injecting the feature branch segment would break routes like /Documents/Agreement/...
 */
const RESTHUB_HOST_FIRST_LABELS = new Set(['api', 'betaapi', 'devapi', 'vtapi']);
/** True when hostname is e.g. devapi.tradingblock.com (first segment listed above). */
const isRestHubHostname = (hostname) => {
    const firstLabel = hostname.split('.')[0];
    return RESTHUB_HOST_FIRST_LABELS.has(firstLabel);
};
/**
 * For feature branch deploys, prepend the branch segment to links that target other TB/MB Docker containers so Nginx can route them.
 * Skips RestHub host (see RESTHUB_HOST_FIRST_LABELS) and non-TB/MB URLs.
 * @param url - The external URL (e.g., 'https://dashboard.tradingblock.com/auth' or 'https://application.tradingblock.com/apply').
 * @returns The URL with branch prefix in the path when applicable (e.g., 'https://dashboard.tradingblock.com/TBFE-1955-2/auth').
 */
const getExternalUrlWithBranchPrefix = (url) => {
    // If not an absolute URL, return as-is (shouldn't happen, but be safe).
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return url;
    }
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.toLowerCase();
        // Check if this is a nginx-handled domain (tradingblock.com subdomains, excluding legacy).
        // Domains like dashboard.tradingblock.com, devdashboard.tradingblock.com, application.tradingblock.com, etc.
        const isNginxHandledDomain = (hostname.endsWith('.tradingblock.com') || hostname.endsWith('.moneyblock.com')) && !hostname.startsWith('legacy.') && !hostname.startsWith('www.');
        // If not a nginx-handled domain, return as-is.
        if (!isNginxHandledDomain) {
            return url;
        }
        // RestHub: never add the branch to the path (paths are not Nginx routes).
        if (isRestHubHostname(hostname)) {
            return url;
        }
        // Get subdirectory prefix.
        const subdirectory = getSubdirectory();
        // If no subdirectory, return as-is (main branch deploy or local dev).
        if (!subdirectory) {
            return url;
        }
        // Check if path already contains the subdirectory prefix to avoid double prefix.
        // This can happen if the URL was already processed or came from a source that included it.
        if (urlObj.pathname.startsWith(subdirectory)) {
            // Path already has the prefix, return as-is.
            return url;
        }
        // Add branch prefix to the path.
        const pathWithPrefix = subdirectory + urlObj.pathname + urlObj.search + urlObj.hash;
        // Reconstruct URL with new path.
        return `${urlObj.protocol}//${urlObj.host}${pathWithPrefix}`;
    }
    catch (error) {
        // If URL parsing fails, return as-is.
        console.warn('[ExternalLink] Failed to parse URL:', url, error);
        return url;
    }
};
const ExternalLinkContent = ({ href, className, onClick, translate, children, styleOverride, }) => {
    const { getTranslationId } = useLocalize();
    const ariaLabel = useMemo(() => {
        const val = translate(getTranslationId({ id: externalLinkTextId }));
        return _.isString(val) ? val : _.startCase(externalLinkTextId);
    }, [translate, getTranslationId]);
    const finalHref = useMemo(() => getExternalUrlWithBranchPrefix(href), [href]);
    return (React.createElement("a", { href: finalHref, className: className, target: "_blank", rel: "noopener noreferrer", "aria-label": `(${ariaLabel})`, onClick: onClick, style: styleOverride }, children));
};
export const ExternalLink = withLocalize(ExternalLinkContent);
//# sourceMappingURL=ExternalLink.js.map